// Thin fetch wrappers used by DocPane's edit-mode save flow. Mirrors
// the inline apiRead() in app.jsx but returns sha256 + mtime (M1
// extended /app/api/read), and adds apiWriteFile for PUT /app/api/file
// with if_sha256 optimistic concurrency.
//
// Errors are typed so the UI can branch without parsing message strings.

export class StalePreconditionError extends Error {
  constructor(message, { currentSha256, currentMtime }) {
    super(message);
    this.name = 'StalePreconditionError';
    this.code = 'STALE_PRECONDITION';
    this.currentSha256 = currentSha256;
    this.currentMtime = currentMtime;
  }
}

export class ReservedPathError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ReservedPathError';
    this.code = 'RESERVED_PATH';
  }
}

export class NetworkError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = 'NetworkError';
    this.status = status;
    this.body = body;
  }
}

const q = (orgSlug, wsHandle) =>
  '?org=' + encodeURIComponent(orgSlug) +
  '&workspace=' + encodeURIComponent(wsHandle);

export async function apiReadFile({ orgSlug, wsHandle, path }) {
  const url = '/app/api/read' + q(orgSlug, wsHandle) + '&path=' + encodeURIComponent(path);
  const r = await fetch(url);
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new NetworkError('/app/api/read ' + r.status, { status: r.status, body });
  }
  const body = await r.json().catch(() => ({}));
  return {
    content: body.content,
    truncated: !!body.truncated,
    sha256: body.sha256,
    mtime: body.mtime,
  };
}

async function parseOrThrow(r, route) {
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(body.message || (route + ' ' + r.status));
    err.code = body.code || body.error;
    err.body = body;
    err.status = r.status;
    throw err;
  }
  return body;
}

export async function apiWriteFile({ orgSlug, wsHandle, path, content, ifSha256 }) {
  const url = '/app/api/file' + q(orgSlug, wsHandle);
  const payload = { path, content };
  if (ifSha256 != null) payload.if_sha256 = ifSha256;
  const r = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await r.json().catch(() => ({}));
  if (r.status === 409 && body.code === 'STALE_PRECONDITION') {
    throw new StalePreconditionError(body.message || 'stale', {
      currentSha256: body.current_sha256,
      currentMtime: body.current_mtime,
    });
  }
  if (r.status === 400 && body.code === 'RESERVED_PATH') {
    throw new ReservedPathError(body.message || 'reserved');
  }
  if (!r.ok) {
    throw new NetworkError('/app/api/file ' + r.status, { status: r.status, body });
  }
  return {
    path: body.path,
    sha256: body.sha256,
    mtime: body.mtime,
    version_id: body.version_id,
  };
}

// ---------- M3 additions ----------

export async function apiMove(orgSlug, wsHandle, from, to, ifSha256) {
  const route = '/app/api/move' + q(orgSlug, wsHandle);
  const payload = ifSha256 ? { from, to, if_sha256: ifSha256 } : { from, to };
  const r = await fetch(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseOrThrow(r, route);
}

export async function apiMoveBatch({ orgSlug, wsHandle, moves }) {
  const route = '/app/api/move-batch' + q(orgSlug, wsHandle);
  const r = await fetch(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ moves }),
  });
  return parseOrThrow(r, route);
}

export async function apiCopy(orgSlug, wsHandle, from, to) {
  const route = '/app/api/copy' + q(orgSlug, wsHandle);
  const r = await fetch(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to }),
  });
  return parseOrThrow(r, route);
}

export async function apiDelete(orgSlug, wsHandle, path, ifSha256) {
  const route = '/app/api/file' + q(orgSlug, wsHandle);
  const payload = ifSha256 ? { path, if_sha256: ifSha256 } : { path };
  const r = await fetch(route, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseOrThrow(r, route);
}

export async function apiRestore(orgSlug, wsHandle, path, sha256) {
  const route = '/app/api/restore' + q(orgSlug, wsHandle);
  const r = await fetch(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, version: sha256 }),
  });
  return parseOrThrow(r, route);
}

export async function apiStat(orgSlug, wsHandle, path) {
  const route = '/app/api/stat' + q(orgSlug, wsHandle)
    + '&path=' + encodeURIComponent(path);
  const r = await fetch(route);
  return parseOrThrow(r, route);
}

export async function apiVersions(orgSlug, wsHandle, path) {
  const route = '/app/api/versions' + q(orgSlug, wsHandle)
    + '&path=' + encodeURIComponent(path);
  const r = await fetch(route);
  return parseOrThrow(r, route);
}

export async function apiRelated(orgSlug, wsHandle, path) {
  const route = '/app/api/related' + q(orgSlug, wsHandle)
    + '&path=' + encodeURIComponent(path);
  const r = await fetch(route);
  return parseOrThrow(r, route);
}

export async function apiNewFile(orgSlug, wsHandle, path, initialContent = '') {
  const route = '/app/api/file' + q(orgSlug, wsHandle);
  const r = await fetch(route, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, content: initialContent }),
  });
  return parseOrThrow(r, route);
}
